import styled from 'styled-components';
import * as yup from 'yup';

import Button from 'components/shared/Button';
import CreatableSelect from 'components/shared/form/CreatableSelect';
import FieldLabel from 'components/shared/form/FieldLabel';
import Input from 'components/shared/form/Input';
import { FormProvider, useForm } from 'context/form';
import { FormHelpers, FormMeta, FormValues } from 'context/form/types';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { HighCostMedicationList, HighCostMedicationTier } from 'models/HighCostMedicationList';
import { HighCostMedicationListItem } from 'models/HighCostMedicationListItem';
import {
  AdminHighCostMedicationListItemMutationParams,
  createAdminHighCostMedicationListItem,
  updateAdminHighCostMedicationListItem,
} from 'services/api/admin/highCostMedicationListItems';
import { useToastActions } from 'stores/toastStore';
import { BodySmall, BodySmallBold } from 'styles/typography';

type HighCostMedListEntryFormValues = {
  genericName: string;
  brandName: string;
  // The tier's free-text label, not an id: typing one is how a tier comes into being.
  costTier: string;
  dosage: string;
  route: string;
  form: string;
};

type HighCostMedListEntryFormMeta = {
  isSubmitting: boolean;
};

// genericName is the only required field: brandName is a match key olio-ai compares against and the
// rest is reviewer context. Duplicate names are allowed on purpose — the same generic legitimately
// appears under several brands at different tiers. See design 001.
const highCostMedListEntrySchema = yup.object({
  genericName: yup.string().trim().required(),
  brandName: yup.string().nullable(),
  costTier: yup.string().nullable(),
  dosage: yup.string().nullable(),
  route: yup.string().nullable(),
  form: yup.string().nullable(),
});

export type HighCostMedListEntryFormProps = {
  highCostMedicationList: HighCostMedicationList;
  highCostMedicationListItem?: HighCostMedicationListItem;
  onClose: () => void;
  // Fired only on a successful write, before onClose — Cancel must not look like a save to the
  // panel behind the form.
  onSaved?: () => void;
};

// Inline add/edit medication form rendered inside the admin High Cost Med List slideout, replacing
// the medication table while it is open. Mirrors AdminDenialListEntryForm: no review step, Cancel
// and submit act directly.
export default function HighCostMedListEntryForm({
  highCostMedicationList,
  highCostMedicationListItem,
  onClose,
  onSaved,
}: HighCostMedListEntryFormProps) {
  const isEdit = !!highCostMedicationListItem;
  const tiers = highCostMedicationList.tiers ?? [];
  // The tab fetches its lists with include=client, so a list reaching this form carries one. A
  // response that somehow does not is caught in handleSubmit rather than turned into a blank id.
  const clientId = highCostMedicationList.client?.id;

  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();

  const { mutate } = useMutateWithToast({
    // clientId resolves the client's active list on create; an edit acts on the medication id and
    // the update endpoint does not permit one, so it is absent from the shared params. handleSubmit
    // refuses a create without a client, so this path only runs with one in hand.
    mutationFn: (params: AdminHighCostMedicationListItemMutationParams) =>
      isEdit
        ? updateAdminHighCostMedicationListItem({ id: highCostMedicationListItem.id, ...params })
        : createAdminHighCostMedicationListItem({ clientId: clientId!, ...params }),
  });

  const handleSubmit = (
    values: FormValues<HighCostMedListEntryFormValues>,
    _meta: FormMeta<HighCostMedListEntryFormMeta>,
    helpers: FormHelpers<HighCostMedListEntryFormValues, HighCostMedListEntryFormMeta>
  ) => {
    helpers.setMeta('isSubmitting', true);

    const genericName = (values.genericName ?? '').trim();

    if (!isEdit && !clientId) {
      helpers.setMeta('isSubmitting', false);
      addToast({ text: 'This list is missing its client, so the medication could not be added' });

      return;
    }

    const params: AdminHighCostMedicationListItemMutationParams = {
      genericName,
      brandName: values.brandName?.trim() || null,
      costTier: values.costTier?.trim() || null,
      dosage: values.dosage?.trim() || null,
      route: values.route?.trim() || null,
      form: values.form?.trim() || null,
    };

    mutate(params, {
      onSuccess: () => {
        invalidateQueries({ adminHighCostMedicationListItems: true, adminHighCostMedicationLists: true });
        addToast({
          text: isEdit
            ? `${genericName} successfully updated`
            : `${genericName} successfully added to the high cost med list`,
        });
        onSaved?.();
        onClose();
      },
      onSettled: () => {
        helpers.setMeta('isSubmitting', false);
      },
    });
  };

  return (
    <FormProvider<HighCostMedListEntryFormValues, HighCostMedListEntryFormMeta>
      onSubmit={handleSubmit}
      yupSchema={highCostMedListEntrySchema}
      initialValues={{
        genericName: highCostMedicationListItem?.genericName ?? '',
        brandName: highCostMedicationListItem?.brandName ?? '',
        costTier: highCostMedicationListItem?.tier?.label ?? '',
        dosage: highCostMedicationListItem?.dosage ?? '',
        route: highCostMedicationListItem?.route ?? '',
        form: highCostMedicationListItem?.form ?? '',
      }}
      initialMeta={{ isSubmitting: false }}>
      <HighCostMedListEntryFields isEdit={isEdit} tiers={tiers} onClose={onClose} />
    </FormProvider>
  );
}

function HighCostMedListEntryFields({
  isEdit,
  tiers,
  onClose,
}: {
  isEdit: boolean;
  tiers: HighCostMedicationTier[];
  onClose: () => void;
}) {
  const { values, setValue, valid, dirty, meta, submitForm } = useForm<
    HighCostMedListEntryFormValues,
    HighCostMedListEntryFormMeta
  >();

  const isSubmitting = !!meta.isSubmitting;

  return (
    <Container>
      <SectionTitle>{isEdit ? 'Edit Medication' : 'Add New High Cost Medication'}</SectionTitle>
      <Description>
        Complete the fields below to {isEdit ? 'edit a medication on' : 'add a new medication to'} this
        organization&rsquo;s high cost medication list
      </Description>

      <Fields>
        <Field>
          <FieldLabel>Medication Name</FieldLabel>
          <Input
            value={values.genericName ?? ''}
            onChange={(e) => setValue('genericName', e.target.value)}
            placeholder='Medication Name'
            disabled={isSubmitting}
          />
        </Field>

        <Field>
          <FieldLabel>Brand Name (optional)</FieldLabel>
          <Input
            value={values.brandName ?? ''}
            onChange={(e) => setValue('brandName', e.target.value)}
            placeholder='Brand Name'
            disabled={isSubmitting}
          />
        </Field>

        {/* The cost tier key is a rollup of what the medications carry, so the list's existing
            labels are offered as a convenience but anything can be typed — that is how a tier comes
            into being. Renders even on an empty key, where typing the first label is the point. */}
        <Field>
          <FieldLabel>Cost Tier (optional)</FieldLabel>
          <CreatableSelect
            options={tiers.map((tier) => tier.label)}
            value={values.costTier ?? ''}
            onChange={(costTier) => setValue('costTier', costTier)}
            placeholder='Select a cost tier'
            disabled={isSubmitting}
            dataTestId='cost-tier-select'
          />
        </Field>

        <Field>
          <FieldLabel>Dosage (optional)</FieldLabel>
          <Input
            value={values.dosage ?? ''}
            onChange={(e) => setValue('dosage', e.target.value)}
            placeholder='Dosage'
            disabled={isSubmitting}
          />
        </Field>

        <Field>
          <FieldLabel>Route (optional)</FieldLabel>
          <Input
            value={values.route ?? ''}
            onChange={(e) => setValue('route', e.target.value)}
            placeholder='Route'
            disabled={isSubmitting}
          />
        </Field>

        <Field>
          <FieldLabel>Form (optional)</FieldLabel>
          <Input
            value={values.form ?? ''}
            onChange={(e) => setValue('form', e.target.value)}
            placeholder='Form'
            disabled={isSubmitting}
          />
        </Field>
      </Fields>

      <Actions>
        <Button variant='ghost' onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant='primary'
          onClick={() => submitForm()}
          loading={isSubmitting}
          disabled={!valid || !dirty || isSubmitting}>
          {isEdit ? 'Save' : 'Add Medication'}
        </Button>
      </Actions>
    </Container>
  );
}

// The form replaces the table directly under the panel header, and PanelContent carries no top
// padding (the list view gets its spacing from the toolbar), so the gap is set here.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 24px;
`;

const SectionTitle = styled(BodySmallBold)`
  font-size: 16px;
  margin-bottom: 8px;
`;

const Description = styled(BodySmall)`
  display: block;
  margin-bottom: 24px;
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 40px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;
